
import "./tournament-round-group.scss";
import {
  defaultGapping,
  defaultLayouts,
  defaultShadows,
  defaultTypography,
  golfstatusColors,
  GSActionBar,
  GSItemList,
} from "golfstatus_react_components";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useRoundActionHooks, getRoundName, getRoundNumberName } from "../../../hooks/round/roundHooks";
import { getRoundAssignedTeamCount } from "../../../hooks/round/roundHoleAssignmentHooks";
import { useSelector } from "react-redux";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";
import TournamentRoundLineItem from "./tournament-round-line-item";

const TournamentRoundGroup = (props) => {
  const { group, roundSelected } = props;

  const { number, rounds } = group;

  //a linked round copies the round it is added from, so a new sub round starts
  //from the first round under this number.
  const { addLinkedRound } = useRoundActionHooks(rounds?.[0]);

  const isLinked = rounds?.length > 1;

  //a team only plays a number once, so what the linked rounds hold between them
  //is what has been taken out of the field
  const currentTournament = useSelector(selectCurrentTournament);
  const assignedTeams =
    rounds?.reduce?.(
      (total, round) => total + getRoundAssignedTeamCount(round),
      0
    ) ?? 0;

  const unassignedTeams = Math.max(
    (currentTournament?.numberRegisteredTeams ?? 0) - assignedTeams,
    0
  );

  //a round's own label names it wherever it is set. without one, only a linked
  //round needs naming, since the header above already reads Round {number}.
  const getRoundLabel = (round) => {
    if (round?.roundName) {
      return `Round ${round?.roundName}`;
    }
    if (isLinked) {
      return `Round ${getRoundNumberName(round, rounds)}`;
    }
    return "";
  };

  //linked rounds are alternatives to each other, so name every one of them, by
  //their own label where they have one. past a handful they read as a range.
  const roundNames = rounds?.map?.((round) => getRoundName(round, rounds)) ?? [];

  const getRoundsPhrase = () => {
    if (roundNames.length === 1) {
      return `Round ${roundNames[0]}`;
    }
    if (roundNames.length === 2) {
      return `Round ${roundNames[0]} or ${roundNames[1]}`;
    }
    if (roundNames.length <= 5) {
      return `one of the following rounds: ${roundNames.join("/")}`;
    }
    return `one of the following rounds: ${roundNames[0]} - ${
      roundNames[roundNames.length - 1]
    }`;
  };

  //an untouched group explains where teams can play, one being worked through
  //says what is left to place, and a finished one says so
  const getGroupDetail = () => {
    if (roundNames.length === 0) {
      return "";
    }

    if (assignedTeams === 0) {
      if (roundNames.length === 1) {
        return `All teams can play in ${getRoundsPhrase()}`;
      }
      if (roundNames.length === 2) {
        return `Teams can play in either ${getRoundsPhrase()}`;
      }
      return `Teams can play in ${getRoundsPhrase()}`;
    }

    if (unassignedTeams === 0) {
      return "All teams are assigned to a hole";
    }

    return `${unassignedTeams} ${
      unassignedTeams === 1 ? "team needs" : "teams need"
    } to be assigned to ${getRoundsPhrase()}`;
  };

  const groupHeader = (
    <div
      style={{
        ...defaultLayouts.vStack,
        ...defaultLayouts.fullWidth,
        ...defaultGapping.smallGap,
      }}
    >
      <div>{`Round ${number}`}</div>
      <div style={{ ...defaultTypography.bodyRegular.withOpacity(0.7) }}>
        {getGroupDetail()}
      </div>
    </div>
  );

  const groupActions = [
    {
      type: "light-grey",
      buttonIcon: faPlus,
      actionClick: addLinkedRound,
    },
  ];

  const getRoundLineItem = (round) => {
    return (
      <TournamentRoundLineItem
        round={round}
        isListItem
        subRoundLabel={getRoundLabel(round)}
      />
    );
  };

  return (
    <tournament-round-group
      style={{ ...defaultLayouts.vStack, ...defaultLayouts.fullWidth }}
    >
      {/* the header holds the top of the list until the next round's group
          pushes it off, the same way the hole assignment headers do */}
      <GSActionBar
        style={{
          ...defaultLayouts.stickyTop,
          ...defaultShadows.secondLayerShadow,
          backgroundColor: golfstatusColors.white,
          zIndex: 1,
        }}
        type="x-large-pad"
        header={groupHeader}
        pageActions={groupActions}
      />
      <GSItemList
        items={rounds}
        listItem={(round) => getRoundLineItem(round)}
        itemSelected={roundSelected}
        type="vertical selectable linked-rounds"
      />
    </tournament-round-group>
  );
};

export default TournamentRoundGroup;
